import interlink1 from '../assets/images/interlink/interlink 1.png';
import interlink2 from '../assets/images/interlink/interlink 2.png';
import checkit1 from '../assets/images/checkit/checkit1.png';
import checkit2 from '../assets/images/checkit/checkit2.png';

// Company work: role, architecture and stack only. No client names, figures,
// screenshots, code or prompts — the `flow` diagram stands in for a screenshot.
export const workProjects = [
	{
		title: 'Multi-Agent Conversational AI',
		label: 'work @ GoRated',
		description:
			"The pipeline behind the company's conversational AI. An orchestrator routes each customer message to specialised agents — FAQ retrieval and lead prequalification — that answer with retrieval-augmented generation over PostgreSQL and MongoDB/DocumentDB. I lead its architecture, and fixed its hybrid (vector + full-text) search so exact-match FAQ answers come back reliably.",
		tags: ['LLMs', 'RAG', 'Multi_Agent', 'Vector_Search', 'Ruby_on_Rails', 'DocumentDB'],
		flow: ['customer message', 'orchestrator', ['faq retrieval', 'lead prequal'], 'grounded reply'],
	},
	{
		title: 'Customer-Support AI Product',
		label: 'work @ GoRated',
		description:
			'Led development and deployment of a customer-support AI product, re-architecting it from a legacy ERP codebase into a standalone service. Owned delivery end-to-end, from development through production on AWS, with Kubernetes and ArgoCD GitOps managing staging and production.',
		tags: ['Ruby_on_Rails', 'GraphQL', 'PostgreSQL', 'Vue.js', 'AWS_EKS', 'Kubernetes', 'ArgoCD'],
		flow: ['legacy erp', 'standalone service', 'argocd gitops → eks', ['staging', 'production']],
	},
	{
		title: 'Help Desk & Ticketing System',
		label: 'work @ GoRated',
		description:
			"A help desk I built as a standalone service. Tickets come in from staff, a customer portal, a public help center, and escalations from the company's AI chatbot; agents triage, assign and answer them with internal notes, email replies and SLA deadlines, and ticket status flows back to the chatbot so the customer hears it on Messenger. Updates are real-time, and it runs on Kubernetes on Akamai Cloud (Linode).",
		tags: ['Ruby_on_Rails', 'GraphQL', 'PostgreSQL', 'Vue.js', 'AnyCable', 'Kubernetes', 'Linode'],
		flow: [['chatbot escalation', 'help center · portal'], 'ticket queue', 'agents + sla', 'status → messenger'],
	},
	{
		title: 'Internal Confluence Replacement',
		label: 'internal tool @ GoRated',
		description:
			"A self-hosted wiki I'm building to move the team off paid Confluence. Whole spaces import with their history, comments and attachments, and every page is checked to round-trip before it's accepted. Pages are written in a block editor with real-time collaborative editing, and standups live in it too: it reads the week's standup pages, flags what's missing or stuck, and drafts the sprint page. An LLM only summarises findings the app has already verified, and its text is labelled as the model's.",
		tags: ['Ruby_on_Rails', 'GraphQL', 'PostgreSQL', 'Vue.js', 'Tiptap', 'Yjs', 'LLMs'],
		flow: ['confluence export', 'fidelity-checked import', ['block editor', 'live collab'], 'standup checks → llm summary'],
	},
	{
		title: 'AI Voice Agent — Outbound Calls',
		label: 'prototype @ GoRated',
		description:
			"An outbound voice agent I built and demoed to prospective clients. It phones customers to confirm their installation appointment: Twilio places the call, and a Node.js audio bridge I wrote streams the live audio to OpenAI's Realtime speech-to-speech model, which confirms the address and schedule in Taglish and records the outcome through a tool call. Because the model speaks directly, a pronunciation layer turns Philippine addresses and dates into spoken form; voicemail detection means unanswered calls cost no model tokens; and the agent says it's automated and verifies identity before reading anyone's address, per the Data Privacy Act. A Vue dashboard shows the call queue and live transcripts.",
		tags: ['Voice_AI', 'OpenAI_Realtime', 'Twilio', 'Node.js', 'Ruby_on_Rails', 'Vue.js', 'TypeScript'],
		flow: ['call queue', 'twilio dials', 'audio bridge', 'realtime voice agent', 'tool call → outcome saved'],
	},
	{
		title: 'AISE — AI Software Engineering',
		label: 'dev workflow @ GoRated',
		description:
			'A development methodology I created and run on Claude Code — "not vibe coding." Specialized agents hand work down a pipeline: an explorer maps the codebase against a team-maintained source of truth, then a planner, an optional designer, a coder, a test strategist and a senior reviewer, which sends work back until both it and the developer are satisfied. Genesis extends it to from-scratch and clone builds: it proves the riskiest path on real data first, then harvests the docs AISE runs on. The team uses it to ship new products, features and hotfixes.',
		tags: ['Claude_Code', 'Multi_Agent', 'Agentic_Workflows', 'Prompt_Engineering'],
		flow: ['spec', 'explore + plan', 'design (optional)', 'code', ['test strategy', 'senior review'], 'dev sign-off'],
	},
];

export const personalProjects = [
	{
		title: 'Piastries',
		label: 'LLM · PWA',
		description:
			'A baking assistant that adapts recipes to a specific oven and learns from every logged bake. The LLM returns structured JSON that code validates before anyone sees it — temperature and time bounds, one retry, then fall back to the source recipe. A second critic pass flags plans that would waste ingredients, and requests fall back to a lighter model when the free tier is busy. Data stays on the phone; hosted for free on Cloudflare Pages.',
		tags: ['React_Native', 'Expo', 'TypeScript', 'Gemini_API', 'Cloudflare_Pages', 'IndexedDB'],
		flow: ['recipe + bake history', 'gemini', ['validate json', 'critic pass'], 'oven-ready plan'],
		github: 'https://github.com/atthmew/piastries',
	},
	{
		title: 'InterLink',
		label: 'MERN Stack & Socket.io',
		description:
			'A social media platform designed to foster genuine connections without the burden of judgment. Unlike traditional networks, InterLink emphasizes user anonymity, creating a safe space free from racism, discrimination, and bias.',
		tags: ['ReactJS', 'NodeJS', 'MongoDB', 'ExpressJS', 'Socket.IO'],
		images: [interlink1, interlink2],
		github: 'https://github.com/atthmew/interlink-mern',
		demo: 'https://www.youtube.com/watch?v=STlR_XQ7X0Q',
	},
	{
		title: 'CheckIT',
		label: 'Capstone · React Native, MERN & Flask',
		description:
			'An Android app that automates examination checking for multiple-choice, true/false, identification, and essay questions, while recording and exporting student scores. It also provides ranking and item analysis, helping professors find areas for improvement.',
		tags: ['React_Native', 'NodeJS', 'MongoDB', 'ExpressJS', 'Flask', 'OpenCV', 'CRNN', 'NLP', 'Sentence_Embedding'],
		images: [checkit1, checkit2],
		demo: 'https://www.youtube.com/watch?v=lYAQEYHdsdI',
	},
];

export const earlyProjects = [
	{
		title: 'Barangay System',
		description: 'Barangay management web app for handling borrowing requests and borrower records.',
		tags: ['EJS', 'NodeJS', 'MongoDB', 'JWT'],
		github: 'https://github.com/atthmew/brgysystem-js',
		demo: 'https://www.youtube.com/watch?v=gtjocsYOeMc',
	},
	{
		title: 'Old Spotify Clone',
		description: 'A clone of the classic Spotify design that plays music from your account via the Spotify API.',
		tags: ['ReactJS', 'spotify-api'],
		github: 'https://github.com/atthmew/old-spotify-clone-reactjs',
		live: 'https://atthmew.github.io/old-spotify-clone-reactjs/',
	},
	{
		title: 'Mov(i)e It',
		description: 'Search any movie and add it to a personal watch list, built on TheMovieDB API.',
		tags: ['ReactJS', 'ReduxJS', 'tmdb-api'],
		github: 'https://github.com/atthmew/movieit-react',
		live: 'https://atthmew.github.io/movieit-react/',
	},
	{
		title: 'Mapty',
		description: 'Running and cycling tracker on a map.',
		tags: ['JavaScript', 'googleapis'],
		github: 'https://github.com/atthmew/mapty',
		live: 'https://atthmew.github.io/mapty/',
	},
	{
		title: 'Omnifood',
		description: 'Landing page design for a healthy food ordering app.',
		tags: ['HTML', 'CSS'],
		github: 'https://github.com/atthmew/omnifood',
		live: 'https://atthmew.github.io/omnifood/',
	},
	{
		title: 'Pig Game',
		description: 'The classic two-player dice game.',
		tags: ['JavaScript'],
		github: 'https://github.com/atthmew/pig-game',
		live: 'https://atthmew.github.io/pig-game/',
	},
	{
		title: 'Guess My Number',
		description: 'Find the secret number in the fewest guesses.',
		tags: ['JavaScript'],
		github: 'https://github.com/atthmew/guess-my-number',
		live: 'https://atthmew.github.io/guess-my-number/',
	},
];
